import { useMentionText } from '@modules/conversations/hooks/use-mention-text';
import { MemberChatPreview } from '@modules/members/members.types';
import { capitalize, capitalizeFirst } from '@shared/utils/capitalize';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { RiAttachment2, RiCornerUpRightDoubleLine, RiImageLine, RiMicLine, RiVideoLine } from 'react-icons/ri';

const MEDIA_PREVIEW = {
  voice: { Icon: RiMicLine, label: 'voice_message' },
  photo: { Icon: RiImageLine, label: 'photo' },
  video: { Icon: RiVideoLine, label: 'video' },
  file: { Icon: RiAttachment2, label: 'media' },
} as const;

const line = 'truncate text-sm leading-4.5 text-text-600 group-[.ant-menu-item-selected]:text-text-700';

type Props = {
  chat?: MemberChatPreview;
  fallback?: string | null;
};

export function ChatPreviewLine({ chat, fallback }: Props) {
  const { t } = useTranslation();
  const toMentionText = useMentionText();

  const speaker = chat?.isSelfSender ? t('you') : chat?.senderName;
  const prefix = speaker ? <span className="font-medium capitalize">{speaker + ': '}</span> : null;
  if (chat?.isDeleted) {
    return (
      <p className={clsx(line, 'italic pr-1')}>
        {capitalizeFirst(t(chat.isSelfSender ? 'you_deleted_a_message' : 'message_removed'))}
      </p>
    );
  }

  if (chat?.media) {
    const { Icon, label } = MEDIA_PREVIEW[chat.media];

    return (
      <span className="flex min-w-0 items-center gap-1.5 text-text-600">
        <Icon className="size-3.5 shrink-0" />
        <span className={line}>
          {prefix}
          {capitalize(t(label))}
        </span>
      </span>
    );
  }

  if (chat?.text) {
    return (
      <p className={line}>
        {prefix}
        {toMentionText(chat.text)}
      </p>
    );
  }

  if (chat?.isForwarded) {
    return (
      <span className="flex min-w-0 items-center gap-1.5 text-text-600">
        <RiCornerUpRightDoubleLine className="size-3.5 shrink-0" />
        <span className={line}>
          {prefix}
          {capitalize(t('forwarded'))}
        </span>
      </span>
    );
  }

  if (!fallback) return null;

  return <p className={clsx(line, 'tracking-[0.04em] uppercase')}>{fallback}</p>;
}
